import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Users, Search } from 'lucide-react';
import Navigation from '../components/Navigation';
import { useTranslation } from 'react-i18next';

const GroupsList = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [groups, setGroups] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const res = await api.get('/groups');
        setGroups(res.data);
        setFilteredGroups(res.data);
      } catch {
        toast.error(t('groupsList.errors.load'));
      } finally {
        setLoading(false);
      }
    };

    fetchGroups();
  }, [t]);

  useEffect(() => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      setFilteredGroups(
        groups.filter(
          (g) =>
            g.name.toLowerCase().includes(term) ||
            g.description.toLowerCase().includes(term)
        )
      );
    } else {
      setFilteredGroups(groups);
    }
  }, [searchTerm, groups]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Navigation />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1
            className="text-4xl font-bold text-slate-900 mb-2"
            data-testid="groups-list-title"
          >
            {t('groupsList.title')}
          </h1>
          <p className="text-slate-600">
            {t('groupsList.subtitle')}
          </p>
        </div>

        <div className="mb-6">
          <div className="relative max-w-md">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400"
              size={20}
            />
            <Input
              type="text"
              placeholder={t('groupsList.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-groups-input"
            />
          </div>
        </div>

        {loading ? (
          <Card>
            <CardContent className="py-12 text-center text-slate-500">
              {t('groupsList.loading')}
            </CardContent>
          </Card>
        ) : filteredGroups.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <h3 className="text-lg font-semibold mb-2">
                {t('groupsList.emptyTitle')}
              </h3>
              <p className="text-slate-600">
                {t('groupsList.emptyDescription')}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredGroups.map((group) => (
              <Card
                key={group.id}
                className="hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(`/groups/${group.id}`)}
                data-testid={`group-list-card-${group.id}`}
              >
                <CardHeader>
                  <div className="flex items-start space-x-4">
                    <img
                      src={
                        group.logo ||
                        `https://ui-avatars.com/api/?name=${group.name}&size=80`
                      }
                      alt={group.name}
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-lg truncate">
                        {group.name}
                      </CardTitle>
                      <CardDescription className="flex items-center mt-1">
                        <Users size={14} className="mr-1" />
                        {group.member_count || 0}{' '}
                        {t('groupsList.membersLabel')}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600 line-clamp-3 mb-4">
                    {group.description}
                  </p>
                  <div className="flex justify-between items-center">
                    <div className="text-sm">
                      {group.membership_price > 0 ? (
                        <span className="font-semibold text-slate-900">
                          {group.currency} {group.membership_price}/
                          {t('groupsList.perYear')}
                        </span>
                      ) : (
                        <span className="text-green-600 font-semibold">
                          {t('groupsList.free')}
                        </span>
                      )}
                    </div>
                    <Button
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/groups/${group.id}`);
                      }}
                      data-testid={`view-details-btn-${group.id}`}
                    >
                      {t('groupsList.viewDetails')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GroupsList;
